import {
  useLayoutEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
  RefObject,
  ChangeEventHandler,
} from 'react'
import { trackInternalEvent } from '@rio/tracking'
import useDebouncedEffect from 'use-debounced-effect'
import { FetchStatuses } from 'builder/modules/constants'
import { apiClient } from 'builder/modules/apiClient'
import Icon, { Icon20 } from 'builder/components/Icon'
import { reflow } from 'builder/utils/reflow'
import { useI18n } from 'builder/hooks/useI18n'
import { useEscapeHandler } from 'builder/hooks/useEscapeHandler'
import { useEffectOnMount } from 'builder/hooks/useEffectOnMount'
import { useForwardedRef } from 'builder/hooks/useForwardedRef'
import { useClickOutsideExcept } from 'builder/hooks/useClickOutsideExcept'
import { useScrollToElementOnMount } from 'builder/hooks/useScrollToElementOnMount'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import useSearch from './hooks/useSearch'
import Placeholder from './Placeholder'
import Phrase from './Phrase'

import {
  Container,
  Header,
  CloseButton,
  SearchIcon,
  SearchField,
  SearchSpinner,
  Content,
  Tags,
  Tag,
  Note,
  Scroll,
  transitionDurationMs,
  Group,
  GroupTitle,
} from './styles'
import { MOBILE_SCROLL_TO_SUGGESTIONS_OFFSET, SCROLL_DELAY_MS } from './constants'
import { PhraseData, ToggleSuggestionsParams } from './types'

type Props = {
  scope: string
  jobTitle?: string
  locale?: string
  text?: string
  wide?: boolean
  elementRef: RefObject<HTMLDivElement>
  outsideOpenerRef: RefObject<HTMLDivElement>
  editorContainerRef: RefObject<HTMLDivElement>
  onSelect?: (item: PhraseData) => void
  onToggleSuggestions: (params: ToggleSuggestionsParams) => void
  isSuggestionsOpen?: boolean
}

export const PhrasesSuggestion = (props: Props) => {
  const {
    scope,
    outsideOpenerRef,
    editorContainerRef,
    elementRef,
    locale = 'en',
    text = '',
    wide = false,
    onSelect = () => {},
    jobTitle = '',
    onToggleSuggestions,
    isSuggestionsOpen,
  } = props
  const { i18n } = useI18n()
  const [isVisible, setVisible] = useState(false)
  const container = useForwardedRef(elementRef)
  const scroll = useRef<HTMLDivElement>(null)
  const [suggestionQuery, setSuggestionQuery] = useState('')
  const { isPhone, isTablet, isLaptop } = useMediaQueries()
  const isSmallDisplay = isPhone || isTablet || isLaptop

  const interactionState = useRef({
    phrase_added: false,
    search_used: false,
    section: scope,
    // TODO: rework/delete this array, or allow sending JSONs in trackInternalEvent
    search_queries: {} as any,
    added_phrase_ids: [] as string[],
    tips_queries: [] as string[],
  })

  const search = useSearch({ scope, locale, query: suggestionQuery })
  const { status, result, categories, categoryFilter, foundAnything, performSearch } = search
  const shouldDisplayPlaceholder = result.length === 0 && status !== FetchStatuses.loaded

  // Start entering transition
  useLayoutEffect(() => {
    if (container.current) {
      reflow(container.current)
    }
    setVisible(true)
  }, [container])

  useEffect(() => {
    setSuggestionQuery('')
  }, [jobTitle])

  // Pre-fills query with the current job title on mount
  useEffectOnMount(() => {
    if (!suggestionQuery && jobTitle) {
      setSuggestionQuery(jobTitle)
    }
  })

  // Scroll the list to top if results are changed
  useLayoutEffect(() => {
    if (scroll.current) {
      scroll.current.scrollTop = 0
    }
  }, [result])

  useScrollToElementOnMount({
    ref: container,
    topOffset: MOBILE_SCROLL_TO_SUGGESTIONS_OFFSET,
    timeoutMs: SCROLL_DELAY_MS + transitionDurationMs,
    isDisabled: !isSmallDisplay,
  })

  // Perform search immediately if user clicked to category
  const handleCategoryClick = useCallback(
    (category: string) => {
      interactionState.current.tips_queries.push(category)
      setSuggestionQuery(category)
      performSearch(category)
    },
    [performSearch],
  )

  const handleItemClick = useCallback(
    (item: PhraseData) => {
      onSelect(item)
      // Track phrase usage to offer a better list of most popular phrases
      apiClient.post(`/phrases/${item.digest}/use?locale=${locale}`)

      interactionState.current.phrase_added = true
      interactionState.current.added_phrase_ids.push(item.digest)
    },
    [onSelect, locale],
  )

  const handleSearch: ChangeEventHandler<HTMLInputElement> = useCallback(
    event => setSuggestionQuery(event.target.value),
    [],
  )

  useDebouncedEffect(
    () => {
      const query = search.query.trim()

      if (query.length) {
        const phraseCount = foundAnything ? result.reduce((acc, g) => acc + g.phrases.length, 0) : 0

        interactionState.current.search_used = true
        interactionState.current.search_queries[query] = phraseCount
      }
    },
    500,
    [search.query, scope, foundAnything, result],
  )

  useClickOutsideExcept(container, [outsideOpenerRef, editorContainerRef], () => {
    if (isSuggestionsOpen) {
      onToggleSuggestions({ isOpen: false })
    }
  })

  useEscapeHandler(() => onToggleSuggestions({ isOpen: false }))

  useEffect(() => {
    const eventParams = interactionState.current

    return () => trackInternalEvent('close_pre_written_phrases_dialog', eventParams)
  }, [])

  // Mark phrases and subphrases as active if they're present in the rich text
  const normalizedResult = useMemo(() => {
    const processPhrase = (item: PhraseData) => {
      let subphrases: PhraseData[] = item.subphrases ? item.subphrases.map(processPhrase) : []
      // Link subphrases with the parent
      subphrases = subphrases.map(s => ({ ...s, isSubphrase: true }))
      // The entire phrase text is added to the content
      const phraseTextAdded = text.includes(item.phrase)
      // All subphrases were used to fill the content
      const allSubphrasesAdded = subphrases.length > 0 && subphrases.every(s => s.isActive)
      // Override and extend phrase/subphrase fields
      return { ...item, subphrases, isActive: phraseTextAdded || allSubphrasesAdded }
    }

    return result.map(group => ({
      ...group,
      phrases: group.phrases.map(processPhrase),
    }))
  }, [result, text])

  return (
    <Container id="suggestionPanel" ref={container} isVisible={isVisible} wide={wide}>
      <Header>
        <SearchIcon />
        <SearchField
          placeholder={i18n.t('builder.resume_editor.phrase_suggestion.search_placeholder')}
          onChange={handleSearch}
          value={suggestionQuery}
        />
        {status !== FetchStatuses.loaded && <SearchSpinner />}
        <CloseButton onClick={() => onToggleSuggestions({ isOpen: false })}>
          <Icon.Close />
        </CloseButton>
      </Header>

      <Content>
        {categories.length > 0 && (
          <Tags>
            {categories.map((category, index) => (
              <Tag
                key={index}
                isActive={categoryFilter === category}
                onClick={() => handleCategoryClick(category)}
              >
                {category}
              </Tag>
            ))}
          </Tags>
        )}

        {foundAnything === false && (
          <Note>{i18n.t('builder.resume_editor.phrase_suggestion.no_results')}</Note>
        )}

        <Scroll ref={scroll}>
          {shouldDisplayPlaceholder && <Placeholder />}

          {!shouldDisplayPlaceholder &&
            normalizedResult.map((group, index) => (
              <Group key={index}>
                <GroupTitle groupType={group.groupType}>
                  {group.groupType === 'most_popular' && <Icon20.Crown />}
                  {group.groupTitle}
                </GroupTitle>
                {group.phrases.map(item => (
                  <Phrase key={item.digest} item={item} onClick={handleItemClick} />
                ))}
              </Group>
            ))}
        </Scroll>
      </Content>
    </Container>
  )
}
